import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useGuidedTour } from "@/context/GuidedTourContext";
import { Button } from "@/components/ui/button";
import { X, ChevronLeft, ChevronRight, RotateCcw, Volume2, VolumeX, MonitorPlay, SkipForward } from "lucide-react";

const PAD = 8;

function useTargetRect(testid, active, stepIndex) {
  const [rect, setRect] = useState(null);
  const measure = useCallback(() => {
    if (!testid) { setRect(null); return; }
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (el) setRect(el.getBoundingClientRect());
    else setRect(null);
  }, [testid]);

  useEffect(() => {
    if (!active) return;
    setRect(null);
    let tries = 0;
    const iv = setInterval(() => {
      measure();
      const el = testid ? document.querySelector(`[data-testid="${testid}"]`) : null;
      tries += 1;
      if (!testid || el || tries > 15) clearInterval(iv);
    }, 140);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => { clearInterval(iv); window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
  }, [active, testid, stepIndex, measure]);

  return rect;
}

export function GuidedTour() {
  const { t } = useTranslation();
  const { session, active, busy, goNext, goBack, goto, end, restart, close, setNarration } = useGuidedTour();
  const audioRef = useRef(null);

  const step = active ? session?.steps?.[session.step] : null;
  const rect = useTargetRect(step?.target, active, session?.step);

  // Optional prerecorded narration — plays if enabled; silently ignores missing audio.
  useEffect(() => {
    if (!active || !step) return;
    const a = audioRef.current;
    if (!a) return;
    a.pause();
    if (session?.narration_enabled && step.audio) {
      a.src = step.audio;
      a.play().catch(() => {}); // autoplay/404 -> silent, captions always shown
    }
  }, [active, step, session?.narration_enabled]);

  if (!active || !step) return null;

  const total = session.total;
  const idx = session.step;
  const isLast = idx >= total - 1;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  const isMobile = vw < 640;
  const cardW = Math.min(380, vw - 24);
  const cardH = Math.min(300, vh - 32);

  // Spotlight cutout box (dims everything else via a huge box-shadow).
  const cut = rect ? {
    position: "fixed", top: rect.top - PAD, left: rect.left - PAD,
    width: rect.width + PAD * 2, height: rect.height + PAD * 2,
    borderRadius: 12, boxShadow: "0 0 0 9999px rgba(15,32,49,0.62)",
    outline: "2px solid #B28A3D", zIndex: 100, pointerEvents: "none",
    transition: "top .2s ease, left .2s ease, width .2s ease, height .2s ease",
  } : null;

  // Card placement that never covers the highlighted region.
  let cardStyle = { width: cardW, maxWidth: "calc(100vw - 24px)" };
  if (!rect) {
    cardStyle = { ...cardStyle, top: "50%", left: "50%", transform: "translate(-50%,-50%)" };
  } else if (isMobile) {
    const targetInTop = rect.top + rect.height / 2 < vh * 0.5;
    cardStyle = targetInTop
      ? { ...cardStyle, left: 12, right: 12, width: "auto", bottom: 16 }
      : { ...cardStyle, left: 12, right: 12, width: "auto", top: 16 };
  } else {
    const spaceBelow = vh - rect.bottom;
    const left = Math.max(16, Math.min(rect.left, vw - cardW - 16));
    if (spaceBelow >= cardH + 14) {
      cardStyle = { ...cardStyle, top: Math.min(vh - cardH - 16, rect.bottom + 14), left };
    } else if (rect.top >= cardH + 14) {
      cardStyle = { ...cardStyle, top: Math.max(16, rect.top - cardH - 14), left };
    } else {
      // Large page targets can occupy nearly the whole viewport. Pin the card inside
      // the viewport instead of calculating a huge bottom offset that clips it above.
      cardStyle = { ...cardStyle, top: 16, left: Math.max(16, vw - cardW - 16) };
    }
  }

  return (
    <div className="fixed inset-0 z-[100]" data-testid="guided-tour-overlay" aria-live="polite">
      <audio ref={audioRef} className="hidden" />
      {/* dim + cutout */}
      {rect ? <div style={cut} data-testid="guided-tour-spotlight" />
            : <div className="absolute inset-0 bg-[#0f2031]/60" />}

      {/* guidance card */}
      <div style={{ position: "fixed", zIndex: 101, ...cardStyle }}
        className="max-h-[calc(100vh-32px)] overflow-y-auto rounded-xl border border-[#B28A3D]/40 bg-white p-4 shadow-2xl" data-testid="guided-tour-card">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-[#B28A3D]">
            <MonitorPlay className="h-3.5 w-3.5" /> {t("presentation.demo_badge")}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setNarration(!session?.narration_enabled)}
              data-testid="guided-tour-narration" title={t("presentation.narration")}
              className="rounded p-1 text-slate-500 hover:bg-slate-100">
              {session?.narration_enabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </button>
            <button onClick={close} data-testid="guided-tour-close" title={t("presentation.close")}
              className="rounded p-1 text-slate-500 hover:bg-slate-100"><X className="h-4 w-4" /></button>
          </div>
        </div>

        <div className="mt-2 font-heading text-base font-bold text-[#17324D]" data-testid="guided-tour-title">{step.title}</div>
        <p className="mt-1 text-sm leading-relaxed text-slate-600" data-testid="guided-tour-body">{step.body}</p>

        <div className="mt-3 flex items-center justify-between">
          <span className="text-[11px] text-slate-400" data-testid="guided-tour-progress">
            {t("presentation.step", { current: idx + 1, total })}
          </span>
          <div className="flex items-center gap-1.5">
            <button onClick={restart} disabled={busy} data-testid="guided-tour-restart"
              className="flex items-center gap-1 rounded px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-100" title={t("presentation.restart")}>
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
            {!isLast && (
              <button onClick={() => goto(total - 1)} disabled={busy} data-testid="guided-tour-skip"
                className="flex items-center gap-1 rounded px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-100" title={t("presentation.skip")}>
                <SkipForward className="h-3.5 w-3.5" /> {t("presentation.skip")}
              </button>
            )}
            <Button size="sm" variant="outline" onClick={goBack} disabled={busy || idx === 0} data-testid="guided-tour-back">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {isLast
              ? <Button size="sm" onClick={end} disabled={busy} data-testid="guided-tour-finish"
                  className="bg-[#17324D] hover:bg-[#17324D]/90">{t("presentation.finish")}</Button>
              : <Button size="sm" onClick={goNext} disabled={busy} data-testid="guided-tour-next"
                  className="bg-[#17324D] hover:bg-[#17324D]/90">{t("presentation.next")} <ChevronRight className="h-4 w-4" /></Button>}
          </div>
        </div>
      </div>
    </div>
  );
}
